'use client'
import { yupResolver } from '@hookform/resolvers/yup'
import { LineUser } from '@qr-front/icons/line-user'
import * as Accordion from '@radix-ui/react-accordion'
import { RESET } from 'jotai/utils'
import { useTranslations } from 'next-intl'
import {
	forwardRef,
	ReactElement,
	SVGProps,
	useImperativeHandle,
	useState,
} from 'react'
import AnimateHeight from 'react-animate-height'
import { FormProvider, Resolver, useForm } from 'react-hook-form'

import { transformBackendResponseKey } from '@/config'
import { getStore } from '@/store'
import { TTranslationFunction } from '@/types'
import { getFirstInvalidInput, validateContentForm } from '@/utils'

import { GENERATOR_FORM_ANIMATION_DURATION } from '../../components/GeneratorFormSection'
import { keepTopEdgeVisibleDuringCollapse } from '../../utils/keepTopEdgeVisibleDuringCollapse'
import { AddressSection } from '../CommonSections/AddressSection'
import {
	contactInfoEmailListAtom,
	contactInfoPhoneListAtom,
	contactInfoWebsiteAtom,
	ContactSection,
} from '../CommonSections/ContactSection'
import {
	addressCityAtom,
	addressCountryAtom,
	addressPostalCodeAtom,
	addressStateAtom,
	addressStreetAtom,
	addressUrlAtom,
	resetAddressForm,
} from '../LocationForm/addressAtoms'
import {
	TContentFormHandle,
	TQrContentFormSection,
} from '../qrContentFormTypes'
import { summaryAtom, SummarySection } from '../SummaryForm/SummarySection'
import { useSyncFormSectionValidity } from '../useSyncFormSectionValidity'
import { getAboutFormSchema, TAboutFormFields } from './aboutFormModel'
import {
	aboutCompanyNameAtom,
	aboutProfessionAtom,
	CompanyAboutSubSection,
} from './CompanyAboutSubSection'
import {
	aboutAvatarImageAtom,
	aboutAvatarImageFileAtom,
	aboutFullNameAtom,
	PersonalAboutSubSection,
} from './PersonalAboutSubSection'

export const ABOUT_FORM_ID = 'about'

export const resetAboutForm = () => {
	// Personal subsection
	getStore().set(aboutFullNameAtom, RESET)
	getStore().set(aboutAvatarImageAtom, RESET)
	getStore().set(aboutAvatarImageFileAtom, RESET)
	// Contact subsection
	getStore().set(contactInfoPhoneListAtom, RESET)
	getStore().set(contactInfoWebsiteAtom, RESET)
	getStore().set(contactInfoEmailListAtom, RESET)
	//	Company subsection
	getStore().set(aboutCompanyNameAtom, RESET)
	getStore().set(aboutProfessionAtom, RESET)
	// Summary subsection
	getStore().set(summaryAtom, RESET)
	// Address subsection
	resetAddressForm()
}

const IconChevron = (props: SVGProps<SVGSVGElement>) => (
	<svg
		width={12}
		height={12}
		viewBox="0 0 12 12"
		fill="none"
		xmlns="http://www.w3.org/2000/svg"
		{...props}
	>
		<path d="m9 6-6.5 5V1z" fill="currentColor" />
	</svg>
)

const SubSection = ({
	id,
	title,
	content,
	isOpen,
}: {
	id: string
	title: string
	content: ReactElement
	isOpen: boolean
}) => (
	<Accordion.Item key={id} value={id}>
		<Accordion.Trigger
			onClick={(event) => {
				if (!isOpen) {
					keepTopEdgeVisibleDuringCollapse(
						event.currentTarget,
						GENERATOR_FORM_ANIMATION_DURATION,
					)
				}
			}}
			className="type-body-medium flex w-full scroll-mt-20 items-center justify-start gap-2 rounded-10 p-4 font-semibold transition-colors data-[state=closed]:bg-light-grey-70 data-[state=open]:bg-transparent tablet:p-6 desktop:scroll-mt-22 [&[data-state=open]>svg]:rotate-90"
		>
			<IconChevron className="transition-transform duration-200" />
			{title}
		</Accordion.Trigger>
		<Accordion.Content forceMount>
			<AnimateHeight
				height={isOpen ? 'auto' : 0}
				disableDisplayNone
				duration={GENERATOR_FORM_ANIMATION_DURATION}
				className="overflow-hidden"
			>
				<div className="p-4 tablet:p-6">{content}</div>
			</AnimateHeight>
		</Accordion.Content>
	</Accordion.Item>
)

export const AboutForm = forwardRef<TContentFormHandle>((_, ref) => {
	const t = useTranslations()

	const formDefaultValues: TAboutFormFields = {
		personal: {
			fullName: getStore().get(aboutFullNameAtom),
		},
		contacts: {
			phone: getStore()
				.get(contactInfoPhoneListAtom)
				?.map((entry) => ({
					value: entry.value,
				})),
			website: getStore().get(contactInfoWebsiteAtom),
			email: getStore()
				.get(contactInfoEmailListAtom)
				?.map((entry) => ({
					value: entry.value,
				})),
		},
		company: {
			companyName: getStore().get(aboutCompanyNameAtom),
			profession: getStore().get(aboutProfessionAtom),
		},
		summary: getStore().get(summaryAtom),
		address: {
			street: getStore().get(addressStreetAtom),
			city: getStore().get(addressCityAtom),
			state: getStore().get(addressStateAtom),
			postalCode: getStore().get(addressPostalCodeAtom),
			country: getStore().get(addressCountryAtom),
			url: getStore().get(addressUrlAtom),
		},
	}

	const form = useForm<TAboutFormFields>({
		defaultValues: formDefaultValues,
		mode: 'onTouched',
		resolver: yupResolver(
			getAboutFormSchema(t),
		) as unknown as Resolver<TAboutFormFields>,
		reValidateMode: 'onChange',
		shouldUnregister: false,
	})

	const {
		formState: { isValid },
		handleSubmit,
	} = form

	const subSectionList = [
		{
			id: 'personal',
			title: t(
				'generator__content_form_section__about__personal_section__title',
			),
			content: <PersonalAboutSubSection />,
		},
		{
			id: 'contacts',
			title: t(
				'generator__content_form_section__about__contact_section__title',
			),
			content: (
				<ContactSection
					phonePath="contacts.phone"
					websitePath="contacts.website"
					emailPath="contacts.email"
					websiteAtom={contactInfoWebsiteAtom}
					fieldGroup="contact_details"
				/>
			),
		},
		{
			id: 'company',
			title: t(
				'generator__content_form_section__about__company_section__title',
			),
			content: <CompanyAboutSubSection />,
		},
		{
			id: 'summary',
			title: t(
				'generator__content_form_section__about__summary_section__title',
			),
			content: <SummarySection />,
		},
		{
			id: 'address',
			title: t(
				'generator__content_form_section__about__address_section__title',
			),
			content: <AddressSection form={form} />,
		},
	] as const satisfies {
		id: keyof TAboutFormFields
		title: string
		content: ReactElement
	}[]

	const [openSection, setOpenSection] = useState<keyof TAboutFormFields>(
		subSectionList[0].id,
	)

	useSyncFormSectionValidity({ formId: ABOUT_FORM_ID, isValid })

	useImperativeHandle(
		ref,
		() => ({
			trigger: (property: string, message: string) => {
				const translatedMessage = t(
					transformBackendResponseKey(message),
				)
				form.setError(property as Parameters<typeof form.setError>[0], {
					message: translatedMessage,
				})
			},
			validate: () =>
				validateContentForm(
					(onValid, onInvalid) =>
						handleSubmit(onValid, async (errors) => {
							const firstError = getFirstInvalidInput(errors)
							const subSectionId =
								firstError?.path.split(/[.[].*/)[0]
							const subSection = subSectionList.find(
								({ id }) => id === subSectionId,
							)
							if (subSection) {
								setOpenSection(subSection.id)
							}
							onInvalid(errors)
						}),
					GENERATOR_FORM_ANIMATION_DURATION * 1.25,
				),
		}),
		[handleSubmit, subSectionList],
	)

	return (
		<Accordion.Root
			type="single"
			collapsible
			value={openSection}
			onValueChange={setOpenSection as (value: string) => void}
			asChild
		>
			<form className="flex flex-col gap-2">
				<FormProvider {...form}>
					{subSectionList.map(({ id, title, content }) => (
						<SubSection
							id={id}
							key={id}
							title={title}
							content={content}
							isOpen={openSection === id}
						/>
					))}
				</FormProvider>
			</form>
		</Accordion.Root>
	)
})

AboutForm.displayName = 'AboutForm'

export const aboutFormConfig = (
	t: TTranslationFunction,
): TQrContentFormSection => ({
	id: ABOUT_FORM_ID,
	title: t('generator__content_form_section__about__title'),
	description: t('generator__content_form_section__about__description'),
	icon: <LineUser />,
	Content: AboutForm,
})
